import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  BarChart3, Users, Eye, Activity, TrendingUp, TrendingDown,
  MousePointerClick, Mail, Download, Image as ImageIcon,
  Database, Plus, Trash2, PenLine,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

/* Brand palette (charcoal + gold editorial) */
const C = { gold: "#B58A45", walnut: "#4B3B31", taupe: "#B6A895", charcoal: "#202020", mist: "#E9E4DC" };
const PLATFORM_COLOR = { instagram: C.gold, facebook: C.walnut, pinterest: C.taupe };

const PLATFORMS = [
  { key: "all", label: "All platforms" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "pinterest", label: "Pinterest" },
];
const RANGES = [
  { key: "7", label: "7 days" },
  { key: "30", label: "30 days" },
  { key: "90", label: "90 days" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

const fmtNum = (n) => {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (a >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return `${n}`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; } };
const todayISO = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v == null ? 0 : Math.max(0, Math.round(Number(v)) || 0));
const ENTRY_PLATFORMS = [{ key: "instagram", label: "Instagram" }, { key: "facebook", label: "Facebook" }, { key: "pinterest", label: "Pinterest" }];
const emptyEntry = () => ({ platform: "instagram", date: todayISO(), followers: "", reach: "", impressions: "", engagements: "", posts: "", clicks: "", signups: "" });
const emptyPost = () => ({ platform: "instagram", date: todayISO(), caption: "", reach: "", likes: "", comments: "", shares: "" });

const Toggle = ({ options, value, onChange, testidPrefix }) => (
  <div className="inline-flex flex-wrap gap-1 rounded-xl bg-secondary/60 p-1">
    {options.map((o) => (
      <button
        key={o.key}
        onClick={() => onChange(o.key)}
        data-testid={`${testidPrefix}-${o.key}`}
        className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${value === o.key ? "bg-[hsl(var(--gold))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Delta = ({ v, unit = "%", testid }) => {
  if (v == null) return null;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`mt-1 inline-flex items-center gap-1 text-xs ${up ? "text-[hsl(var(--gold))]" : "text-destructive"}`} data-testid={testid}>
      <Icon size={12} />{up ? "+" : ""}{v}{unit}<span className="text-muted-foreground"> vs prev</span>
    </span>
  );
};

const Kpi = ({ label, value, icon: Icon, sub, subTone, delta, testid }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      {Icon && <Icon size={16} className="text-[hsl(var(--gold))]" />}
    </div>
    <p className="mt-3 font-display text-3xl leading-none tabular-nums">{value}</p>
    {sub && (
      <p className={`mt-2 text-xs ${subTone === "up" ? "text-[hsl(var(--gold))]" : subTone === "down" ? "text-destructive" : "text-muted-foreground"}`}>{sub}</p>
    )}
    {delta ? <div>{delta}</div> : null}
  </Card>
);

const ChartCard = ({ title, children, right }) => (
  <Card className="p-5 border-border shadow-[var(--shadow-sm)]">
    <div className="mb-4 flex items-center justify-between">
      <h4 className="font-display text-lg">{title}</h4>
      {right}
    </div>
    {children}
  </Card>
);

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid rgba(32,32,32,0.10)",
  background: "#FAF8F3",
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(32,32,32,0.10)",
};

export const BISocial = () => {
  const [platform, setPlatform] = useState("all");
  const [range, setRange] = useState("all");
  const [customStart, setCustomStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); });
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [manageOpen, setManageOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [posts, setPosts] = useState([]);
  const [entryForm, setEntryForm] = useState(emptyEntry());
  const [postForm, setPostForm] = useState(emptyPost());
  const [savingEntry, setSavingEntry] = useState(false);
  const [savingPost, setSavingPost] = useState(false);

  const queryParams = useCallback(() => {
    const p = { platform, range };
    if (range === "custom") { p.start = customStart; p.end = customEnd; }
    return p;
  }, [platform, range, customStart, customEnd]);

  const load = useCallback(() => {
    if (range === "custom" && (!customStart || !customEnd)) return;
    setLoading(true);
    api.get("/superadmin/bi/social", { params: queryParams() })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load social data."))
      .finally(() => setLoading(false));
  }, [range, customStart, customEnd, queryParams]);
  useEffect(() => { load(); }, [load]);

  const loadEntries = useCallback(() => {
    api.get("/superadmin/bi/social/entries").then(({ data }) => { setEntries(data.entries || []); setPosts(data.posts || []); }).catch(() => {});
  }, []);
  useEffect(() => { if (manageOpen) loadEntries(); }, [manageOpen, loadEntries]);

  const setEF = (k, v) => setEntryForm((f) => ({ ...f, [k]: v }));
  const setPF = (k, v) => setPostForm((f) => ({ ...f, [k]: v }));

  const saveEntry = async () => {
    if (entryForm.followers === "" || Number(entryForm.followers) < 0) return toast.error("Enter a follower count.");
    setSavingEntry(true);
    try {
      await api.post("/superadmin/bi/social/entry", {
        platform: entryForm.platform, date: entryForm.date,
        followers: num(entryForm.followers), reach: num(entryForm.reach), impressions: num(entryForm.impressions),
        engagements: num(entryForm.engagements), posts: num(entryForm.posts), clicks: num(entryForm.clicks), signups: num(entryForm.signups),
      });
      toast.success("Snapshot saved.");
      setEntryForm((f) => ({ ...emptyEntry(), platform: f.platform }));
      loadEntries(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save."); }
    finally { setSavingEntry(false); }
  };

  const editEntry = (e) => setEntryForm({
    platform: e.platform, date: e.date, followers: `${e.followers ?? ""}`, reach: `${e.reach ?? ""}`,
    impressions: `${e.impressions ?? ""}`, engagements: `${e.engagements ?? ""}`, posts: `${e.posts ?? ""}`,
    clicks: `${e.clicks ?? ""}`, signups: `${e.signups ?? ""}`,
  });

  const deleteEntry = async (id) => {
    try { await api.delete(`/superadmin/bi/social/entry/${id}`); loadEntries(); load(); toast.success("Snapshot removed."); }
    catch { toast.error("Could not delete."); }
  };

  const savePost = async () => {
    if (!postForm.caption.trim()) return toast.error("Add a short caption.");
    setSavingPost(true);
    try {
      await api.post("/superadmin/bi/social/post", {
        platform: postForm.platform, date: postForm.date, caption: postForm.caption.trim(),
        reach: num(postForm.reach), likes: num(postForm.likes), comments: num(postForm.comments), shares: num(postForm.shares),
      });
      toast.success("Post added.");
      setPostForm((f) => ({ ...emptyPost(), platform: f.platform }));
      loadEntries(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save."); }
    finally { setSavingPost(false); }
  };

  const deletePost = async (id) => {
    try { await api.delete(`/superadmin/bi/social/post/${id}`); loadEntries(); load(); }
    catch { toast.error("Could not delete."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/superadmin/bi/social/export.csv", { params: queryParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `social_${platform}_${range}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const k = data?.kpis;
  const cmp = data?.comparison?.has_comparison ? data.comparison.deltas : null;
  const breakdown = data?.breakdown || [];
  const totalFollowers = useMemo(() => breakdown.reduce((s, b) => s + b.followers, 0), [breakdown]);

  return (
    <div className="space-y-6" data-testid="bi-social">
      {/* Header + controls */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 size={20} className="text-[hsl(var(--gold))]" />
            <h2 className="font-display text-2xl">Social Media</h2>
            {data?.has_real_data ? (
              <Badge variant="secondary" className="border border-[hsl(var(--gold))] text-[hsl(var(--gold))]" data-testid="bi-data-badge">Your data</Badge>
            ) : (
              <Badge variant="secondary" className="text-muted-foreground" data-testid="bi-data-badge">No data yet</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Instagram · Facebook · Pinterest — reach, engagement, and growth at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setManageOpen(true)} className="rounded-xl" data-testid="bi-manage-data"><Database size={15} className="mr-1.5" />Manage data</Button>
          <Button variant="outline" className="rounded-xl" onClick={exportCsv} data-testid="bi-export-csv"><Download size={15} className="mr-1.5" />Export CSV</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Toggle options={PLATFORMS} value={platform} onChange={setPlatform} testidPrefix="bi-platform" />
        <Toggle options={RANGES} value={range} onChange={setRange} testidPrefix="bi-range" />
      </div>

      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-secondary/30 p-3" data-testid="bi-custom-range">
          <label className="space-y-1"><span className="text-xs text-muted-foreground">From</span>
            <Input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="h-9 rounded-lg" data-testid="bi-custom-start" />
          </label>
          <label className="space-y-1"><span className="text-xs text-muted-foreground">To</span>
            <Input type="date" value={customEnd} min={customStart} max={todayISO()} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 rounded-lg" data-testid="bi-custom-end" />
          </label>
          {data?.window && <span className="pb-2 text-xs text-muted-foreground">Comparing to {data.previous_window?.start} → {data.previous_window?.end}</span>}
        </div>
      )}

      {data && !data.has_real_data && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-[color:rgba(181,138,69,0.35)] bg-[hsl(var(--gold)/0.06)] p-4" data-testid="bi-empty-banner">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">No data yet.</span> Add your real numbers to bring this dashboard to life — your metrics will appear as soon as you save your first snapshot.
          </p>
          <Button onClick={() => setManageOpen(true)} className="rounded-xl" data-testid="bi-banner-add"><Plus size={15} className="mr-1.5" />Add your data</Button>
        </Card>
      )}

      {loading && !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-[var(--radius)] bg-secondary/60" />)}
        </div>
      ) : k ? (
        <>
          {/* KPI cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="bi-kpi-followers" label="Followers" value={fmtNum(k.followers)} icon={Users}
              sub={`${k.follower_growth >= 0 ? "+" : ""}${fmtNum(k.follower_growth)} (${k.follower_growth_pct}%) this period`}
              subTone={k.follower_growth >= 0 ? "up" : "down"}
              delta={cmp && <Delta v={cmp.followers} testid="bi-delta-followers" />} />
            <Kpi testid="bi-kpi-reach" label="Reach" value={fmtNum(k.reach)} icon={Eye} sub={`${fmtNum(k.impressions)} impressions`}
              delta={cmp && <Delta v={cmp.reach} testid="bi-delta-reach" />} />
            <Kpi testid="bi-kpi-engagement" label="Engagement Rate" value={`${k.engagement_rate}%`} icon={Activity} sub={`${fmtNum(k.engagements)} interactions`}
              delta={cmp && <Delta v={cmp.engagement_rate} unit=" pp" testid="bi-delta-engagement" />} />
            <Kpi testid="bi-kpi-posts" label="Posts Published" value={fmtNum(k.posts)} icon={ImageIcon} sub={`${fmtNum(k.clicks)} link clicks`}
              delta={cmp && <Delta v={cmp.posts} testid="bi-delta-posts" />} />
          </div>

          {/* Followers over time */}
          <ChartCard title="Audience growth">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gFollow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.gold} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={C.gold} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6459" }} minTickGap={28} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 11, fill: "#6b6459" }} width={44} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtNum(v), "Followers"]} labelFormatter={fmtDate} />
                <Area type="monotone" dataKey="followers" stroke={C.gold} strokeWidth={2.5} fill="url(#gFollow)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Audience by platform (donut) */}
            <ChartCard title="Audience by platform">
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="55%" height={200}>
                  <PieChart>
                    <Pie data={breakdown} dataKey="followers" nameKey="label" innerRadius={52} outerRadius={82} paddingAngle={2} stroke="none">
                      {breakdown.map((b) => <Cell key={b.platform} fill={PLATFORM_COLOR[b.platform]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtNum(v) + " followers", n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {breakdown.map((b) => (
                    <div key={b.platform} className="flex items-center justify-between text-sm" data-testid={`bi-breakdown-${b.platform}`}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: PLATFORM_COLOR[b.platform] }} />
                        {b.label}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {fmtNum(b.followers)}<span className="ml-1 text-xs">({totalFollowers ? Math.round((b.followers / totalFollowers) * 100) : 0}%)</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </ChartCard>

            {/* Newsletter signups by platform */}
            <ChartCard title="Newsletter signups by platform">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={breakdown} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b6459" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtNum} tick={{ fontSize: 11, fill: "#6b6459" }} width={40} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtNum(v), "Signups"]} cursor={{ fill: "rgba(181,138,69,0.08)" }} />
                  <Bar dataKey="signups" radius={[6, 6, 0, 0]}>
                    {breakdown.map((b) => <Cell key={b.platform} fill={PLATFORM_COLOR[b.platform]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Engagement over time */}
          <ChartCard title="Engagement over time">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gEng" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.walnut} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={C.walnut} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(32,32,32,0.06)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6b6459" }} minTickGap={28} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 11, fill: "#6b6459" }} width={44} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtNum(v), "Interactions"]} labelFormatter={fmtDate} />
                <Area type="monotone" dataKey="engagements" stroke={C.walnut} strokeWidth={2.5} fill="url(#gEng)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Top posts */}
          <ChartCard title="Top-performing posts">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-4">Post</th>
                    <th className="py-2 pr-4">Platform</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Reach</th>
                    <th className="py-2 pr-4">Likes</th>
                    <th className="py-2 pr-4">Comments</th>
                    <th className="py-2 pr-4">Eng. rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_posts || []).map((p) => (
                    <tr key={p.id} className="border-t border-border" data-testid={`bi-post-${p.id}`}>
                      <td className="max-w-xs py-3 pr-4"><span className="line-clamp-1 text-foreground">{p.caption}</span></td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: PLATFORM_COLOR[p.platform] }} />
                          {p.platform_label}
                        </span>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">{fmtDate(p.date)}</td>
                      <td className="py-3 pr-4 tabular-nums text-muted-foreground">{fmtNum(p.reach)}</td>
                      <td className="py-3 pr-4 tabular-nums text-muted-foreground">{fmtNum(p.likes)}</td>
                      <td className="py-3 pr-4 tabular-nums text-muted-foreground">{fmtNum(p.comments)}</td>
                      <td className="py-3 pr-4 tabular-nums font-medium text-[hsl(var(--gold))]">{p.engagement_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      ) : null}

      {/* Manage data dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="bi-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Manage social data</DialogTitle>
          </DialogHeader>

          {/* Snapshot entry form */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <PenLine size={16} className="text-[hsl(var(--gold))]" />
              <h4 className="font-medium">Add or update a snapshot</h4>
            </div>
            <div className="rounded-xl border border-[color:rgba(181,138,69,0.35)] bg-[hsl(var(--gold)/0.06)] p-3 text-xs leading-relaxed text-muted-foreground">
              Enter <span className="font-medium text-foreground">whole numbers (counts)</span> straight from each app's Insights — <span className="font-medium text-foreground">not percentages</span>. Engagement rate, growth %, and "vs previous" are all calculated for you. Only <span className="font-medium text-foreground">Followers</span> is required; leave anything you don't have blank. Log the same platform + date again to update it.
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <label className="space-y-1"><span className="text-xs font-medium">Platform</span>
                <select value={entryForm.platform} onChange={(e) => setEF("platform", e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm" data-testid="bi-entry-platform">
                  {ENTRY_PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <span className="block text-[11px] text-muted-foreground">Which account</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Date</span>
                <Input type="date" value={entryForm.date} onChange={(e) => setEF("date", e.target.value)} className="rounded-xl" data-testid="bi-entry-date" />
                <span className="block text-[11px] text-muted-foreground">Day these numbers are for</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Followers *</span>
                <Input type="number" min="0" placeholder="e.g. 3120" value={entryForm.followers} onChange={(e) => setEF("followers", e.target.value)} className="rounded-xl" data-testid="bi-entry-followers" />
                <span className="block text-[11px] text-muted-foreground">Total followers right now</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Reach</span>
                <Input type="number" min="0" placeholder="e.g. 8400" value={entryForm.reach} onChange={(e) => setEF("reach", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground">Unique accounts reached</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Impressions</span>
                <Input type="number" min="0" placeholder="e.g. 12500" value={entryForm.impressions} onChange={(e) => setEF("impressions", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground">Total times seen</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Engagements</span>
                <Input type="number" min="0" placeholder="e.g. 480" value={entryForm.engagements} onChange={(e) => setEF("engagements", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground">Likes + comments + shares + saves</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Posts</span>
                <Input type="number" min="0" placeholder="e.g. 5" value={entryForm.posts} onChange={(e) => setEF("posts", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground"># posts/pins published</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Link clicks</span>
                <Input type="number" min="0" placeholder="e.g. 110" value={entryForm.clicks} onChange={(e) => setEF("clicks", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground">Clicks to your website/link</span>
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Signups</span>
                <Input type="number" min="0" placeholder="e.g. 18" value={entryForm.signups} onChange={(e) => setEF("signups", e.target.value)} className="rounded-xl" />
                <span className="block text-[11px] text-muted-foreground">Newsletter signups from here</span>
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveEntry} disabled={savingEntry} className="rounded-xl" data-testid="bi-entry-save"><Plus size={15} className="mr-1.5" />{savingEntry ? "Saving…" : "Save snapshot"}</Button>
            </div>
          </div>

          {/* Existing entries */}
          <div className="mt-2 rounded-xl border border-border">
            <div className="max-h-56 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-secondary/60">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Date</th><th className="px-3 py-2">Platform</th><th className="px-3 py-2">Followers</th>
                    <th className="px-3 py-2">Reach</th><th className="px-3 py-2">Eng.</th><th className="px-3 py-2">Signups</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && <tr><td colSpan={7} className="px-3 py-5 text-center text-muted-foreground italic">No snapshots yet.</td></tr>}
                  {entries.map((e) => (
                    <tr key={e.id} className="border-t border-border" data-testid={`bi-entry-row-${e.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{e.date}</td>
                      <td className="px-3 py-2">{ENTRY_PLATFORMS.find((p) => p.key === e.platform)?.label || e.platform}</td>
                      <td className="px-3 py-2 tabular-nums">{fmtNum(e.followers)}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{fmtNum(e.reach)}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{fmtNum(e.engagements)}</td>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{fmtNum(e.signups)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => editEntry(e)} className="mr-2 text-muted-foreground hover:text-foreground" title="Edit"><PenLine size={15} /></button>
                        <button onClick={() => deleteEntry(e.id)} className="text-destructive hover:opacity-80" title="Delete" data-testid={`bi-entry-del-${e.id}`}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Optional top posts */}
          <div className="mt-5 space-y-3 border-t border-border pt-5">
            <h4 className="font-medium">Notable posts <span className="text-xs font-normal text-muted-foreground">(optional — powers the "Top-performing posts" table)</span></h4>
            <p className="-mt-1 text-[11px] text-muted-foreground">Reach, likes, comments and shares are whole-number counts for that single post.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <label className="space-y-1 col-span-2 sm:col-span-1"><span className="text-xs text-muted-foreground">Platform</span>
                <select value={postForm.platform} onChange={(e) => setPF("platform", e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm" data-testid="bi-post-platform">
                  {ENTRY_PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </label>
              <label className="space-y-1"><span className="text-xs text-muted-foreground">Date</span>
                <Input type="date" value={postForm.date} onChange={(e) => setPF("date", e.target.value)} className="rounded-xl" />
              </label>
              <label className="space-y-1 col-span-2"><span className="text-xs text-muted-foreground">Caption</span>
                <Input value={postForm.caption} onChange={(e) => setPF("caption", e.target.value)} placeholder="Short caption or note" className="rounded-xl" data-testid="bi-post-caption" />
              </label>
              <label className="space-y-1"><span className="text-xs text-muted-foreground">Reach</span>
                <Input type="number" min="0" value={postForm.reach} onChange={(e) => setPF("reach", e.target.value)} className="rounded-xl" />
              </label>
              <label className="space-y-1"><span className="text-xs text-muted-foreground">Likes</span>
                <Input type="number" min="0" value={postForm.likes} onChange={(e) => setPF("likes", e.target.value)} className="rounded-xl" />
              </label>
              <label className="space-y-1"><span className="text-xs text-muted-foreground">Comments</span>
                <Input type="number" min="0" value={postForm.comments} onChange={(e) => setPF("comments", e.target.value)} className="rounded-xl" />
              </label>
              <label className="space-y-1"><span className="text-xs text-muted-foreground">Shares</span>
                <Input type="number" min="0" value={postForm.shares} onChange={(e) => setPF("shares", e.target.value)} className="rounded-xl" />
              </label>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={savePost} disabled={savingPost} className="rounded-xl" data-testid="bi-post-save"><Plus size={15} className="mr-1.5" />{savingPost ? "Saving…" : "Add post"}</Button>
            </div>
            {posts.length > 0 && (
              <div className="space-y-1.5">
                {posts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                    <span className="line-clamp-1"><span className="text-muted-foreground">{ENTRY_PLATFORMS.find((x) => x.key === p.platform)?.label} · {p.date} · </span>{p.caption}</span>
                    <button onClick={() => deletePost(p.id)} className="ml-3 shrink-0 text-destructive hover:opacity-80" title="Delete"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BISocial;
